import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api.service';
import { Provider } from '../types/provider.types';
import JsonView from '@uiw/react-json-view';
import './DirectScheduling.css';

const DirectScheduling: React.FC = () => {
  const { npi } = useParams<{ npi: string }>();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loginId, setLoginId] = useState('');
  const [patientId, setPatientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (npi) {
      fetchProviderDetails();
    }
  }, [npi]);

  const fetchProviderDetails = async () => {
    try {
      const data = await apiService.getProviderByNPI(npi!);
      setProvider(data);
    } catch (err: any) {
      console.error('Error fetching provider:', err);
      setError('Failed to load provider details');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setApiResponse(null);
    setLoading(true);

    try {
      const response = await apiService.getVisitTypeAndLocationForProvider({
        loginId,
        patientId,
        patientIdType: 'External',
        scheduleType: 'DirectSchedule',
        providerId: npi!,
        providerIdType: 'NPI'
      });
      setApiResponse(response);
    } catch (err: any) {
      console.error('Error fetching visit types:', err);
      setError(err.message || 'Failed to fetch visit types and locations');
    } finally {
      setLoading(false);
    }
  };

  if (!provider) {
    return (
      <div className="direct-scheduling-container">
        <div className="loading">Loading provider details...</div>
      </div>
    );
  }

  const providerName = provider.DisplayName || provider.displayName ||
    `${provider.FirstName || provider.firstName || ''} ${provider.LastName || provider.lastName || ''}`.trim();
  const providerDegrees = (provider.Degrees || provider.degrees || [])
    .map((deg: any) => deg.Degree || deg.degree)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="direct-scheduling-container">
      <div className="direct-scheduling-card">
        <div className="page-header">
          <h2>Verify Direct Scheduling</h2>
          <button onClick={() => navigate(`/provider/${npi}`)} className="btn btn-secondary">
            Back to Provider Details
          </button>
        </div>

        {/* Provider Header Card - Reused from ProviderDetail */}
        <div className="provider-header-card">
          {(provider.Photo || provider.photo) && (
            <img
              src={provider.Photo || provider.photo}
              alt={providerName}
              className="provider-photo"
            />
          )}
          <div className="provider-header-info">
            <h1>{providerName}</h1>
            {providerDegrees && <span className="degrees">{providerDegrees}</span>}
            <div className="header-meta">
              <span>
                <strong>NPI:</strong> {provider.NPI || provider.npi || 'N/A'}
              </span>
              <span>
                <strong>Type:</strong> {provider.ProviderType || provider.providerType || 'N/A'}
              </span>
              <span>
                <strong>Direct Scheduling:</strong>{' '}
                {provider.IsDirectSchedulingAvailable || provider.isDirectSchedulingAvailable
                  ? 'Available'
                  : 'Not Available'}
              </span>
            </div>
          </div>
        </div>

        {/* Direct Scheduling Form */}
        <div className="scheduling-form-section">
          <h3>Enter Patient Information</h3>
          <p className="form-description">
            This tool will verify direct scheduling availability by calling the GetVisitTypeAndLocationForProvider API endpoint.
          </p>

          <form onSubmit={handleSubmit} className="scheduling-form">
            <div className="form-group">
              <label htmlFor="loginId">Login ID</label>
              <input
                type="text"
                id="loginId"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                required
                placeholder="Enter Login ID"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="patientId">Patient ID</label>
              <input
                type="text"
                id="patientId"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                required
                placeholder="Enter Patient ID"
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Verifying...' : 'Submit'}
            </button>
          </form>

          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        {/* API Response Display */}
        {apiResponse && (
          <div className="api-response-section">
            <div className="response-header">
              <h3>API Response</h3>
            </div>
            <div className="json-viewer-wrapper">
              <JsonView value={apiResponse} collapsed={1} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectScheduling;
